import { FC, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Spinner } from '@chakra-ui/react';
import { useQuery } from 'react-query';
import { css } from '@emotion/react';
import { useParams, useHistory } from 'react-router-dom';
import { SortingRule } from 'react-table';
import { toast } from 'react-toastify';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import Page from '../Page';
import Header from '../Header';
import { getCollection, Collection, CollectionInvestment, getCollectionAllocation } from '../../apis/backend';
import ErrorSection from '../ErrorSection';
import { getCollectionImages } from '../../utils/getImages';
import FeedbackFooter from '../FeedbackFooter';
import {
  FilterKey,
  InvestmentTable,
  ColumnSelection,
  ColumnSelectionType,
  translateIdToSelection,
} from '../InvestmentTable';
import { spacing, fontStyles, colors } from '../../styles';
import * as stores from '../../stores';
import CopyIcon from './copy.svg';

const Content: FC<Collection & { collectionId: string; navigateToChat: (message?: string) => void }> = ({
  collectionId,
  name,
  slug,
  imageId,
  description,
  defaultMetric,
  defaultSortMetric,
  defaultSortDesc,
  whyItMatters,
  investments,
  updated,
  investmentStyle,
  timeHorizon,
  navigateToChat,
}) => {
  const { t } = useTranslation();
  // Temporary: Use test data if API doesn't return values yet
  const displayInvestmentStyle = investmentStyle || t('collections.defaultInvestmentStyle');
  const displayTimeHorizon = timeHorizon || t('collections.defaultTimeHorizon');
  // Fetch allocation data to get strategy
  const allocationQuery = useQuery(['collection-allocation', collectionId], () =>
    getCollectionAllocation(collectionId),
  );

  // Merge allocation data into investments
  const investmentsWithAllocations: (CollectionInvestment & { allocation_percentage?: number })[] = investments.map(
    (inv) => {
      if (!allocationQuery.data?.allocations) {
        return inv;
      }

      const allocation = allocationQuery.data.allocations.find((a) => a.symbol === inv.symbol);
      if (allocation && typeof allocation.allocation === 'number') {
        return {
          ...inv,
          // Map API field 'allocation' to storage field 'allocation_percentage'
          allocation_percentage: allocation.allocation,
          rationale: allocation.rationale,
        };
      }
      return inv;
    },
  );

  // Analyze collection composition to determine which filters to show
  const hasStocks = investments.some((inv) => inv.category && ['STOCK', 'ADR'].includes(inv.category));
  const hasFunds = investments.some((inv) => inv.category && ['MUTUAL_FUND', 'ETF'].includes(inv.category));
  const showAssetTypeFilters = hasStocks && hasFunds;

  // Determine which filters are available for this collection
  const availableFilters: FilterKey[] = [
    ...(showAssetTypeFilters ? (['stocks', 'funds'] as FilterKey[]) : []),
    'large' as FilterKey,
    ...(hasFunds ? (['lowFees'] as FilterKey[]) : []),
    'quality' as FilterKey,
  ];

  const [isExpanded, setIsExpanded] = useState(false);
  const [columnSelections, setColumnSelections] = useState<ColumnSelection[]>(
    window.history.state?.columnSelections || [
      { type: ColumnSelectionType.Name },
      { type: ColumnSelectionType.None },
      translateIdToSelection(defaultMetric),
    ],
  );

  const [sortingRule, setSortingRule] = useState<SortingRule<string>>(
    defaultSortMetric ? { id: defaultSortMetric, desc: defaultSortDesc } : { id: 'allocation_percentage', desc: true },
  );
  const userChangedSort = useRef(false);
  const [filters, setFilters] = useState<Partial<Record<FilterKey, boolean>>>(
    window.history.state?.filters || Object.fromEntries(availableFilters.map((key) => [key, false])),
  );
  const [imageSrc, setImageSrc] = useState(getCollectionImages(imageId));

  // Debounced toast to prevent spam when rapidly clicking filters
  const showFilterToastRef = useRef(
    debounce((message: string) => {
      toast(message);
    }, 300),
  );
  const showFilterToast = useCallback((message: string) => {
    showFilterToastRef.current(message);
  }, []);

  // Cleanup debounced function on unmount to prevent firing after component unmounts
  useEffect(() => {
    const debouncedFn = showFilterToastRef.current;
    return () => {
      debouncedFn.cancel();
    };
  }, []);

  useEffect(() => {
    window.history.replaceState({ columnSelections, filters }, '');
  }, [columnSelections, filters]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Update sort based on allocation data - use allocation if available, otherwise name
  useEffect(() => {
    if (!allocationQuery.isSuccess || userChangedSort.current) return;

    if (allocationQuery.data?.allocations && allocationQuery.data.allocations.length > 0) {
      setSortingRule({ id: 'allocation_percentage', desc: true });
    } else {
      setSortingRule({ id: 'name', desc: false });
    }
  }, [allocationQuery.isSuccess, allocationQuery.data]);

  useEffect(() => {
    const handleThemeChange = () => {
      setImageSrc(getCollectionImages(imageId));
    };

    window.addEventListener('themeChanged', handleThemeChange);
    return () => window.removeEventListener('themeChanged', handleThemeChange);
  }, [imageId]);

  return (
    <>
      {/* Title and icon section */}
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: ${spacing.deci};
          margin-bottom: ${spacing.centi};
        `}
      >
        <div
          css={css`
            flex: 1;
          `}
        >
          <h1
            css={css`
              ${fontStyles.shark}
              margin: 0;
              margin-bottom: ${spacing.milli};
              color: ${colors.tungsten};

              html[data-theme='dark'] & {
                color: ${colors.sugar};
              }
            `}
          >
            {t(`collections.names.${slug}`, { defaultValue: name })}
          </h1>

          {/* Tags section */}
          {(displayInvestmentStyle || displayTimeHorizon) && (
            <div
              css={css`
                ${fontStyles.cat}
                color: ${colors.silver};
                html[data-theme='dark'] & {
                  color: ${colors.cloud};
                }
              `}
            >
              {displayInvestmentStyle}
              {displayInvestmentStyle && displayTimeHorizon && ' • '}
              {displayTimeHorizon}
            </div>
          )}
        </div>

        <img
          src={imageSrc}
          css={css`
            height: 80px;
            width: 80px;
            object-fit: contain;
            flex-shrink: 0;
          `}
        />
      </div>

      {/* Description section */}
      <div
        css={css`
          margin-bottom: ${spacing.centi};
        `}
      >
        <div
          css={css`
            ${fontStyles.thinFlea}
            color: ${colors.tungsten};
            line-height: 1.5;
            margin: 0;
            html[data-theme='dark'] & {
              color: ${colors.sugar};
            }
          `}
        >
          {description}
          {updated && ` ${t('collections.updatedAgo', { time: updated })}`}{' '}
          {!isExpanded && (
            <button
              onClick={() => setIsExpanded(true)}
              css={css`
                background: none;
                border: none;
                color: ${colors.petrolBlue};
                cursor: pointer;
                padding: 0;
                ${fontStyles.flea}
                font-weight: 500;

                html[data-theme='dark'] & {
                  color: ${colors.skyBlue};
                }

                &:hover {
                  text-decoration: underline;
                }
              `}
            >
              {t('collections.readMore')}
            </button>
          )}
        </div>
      </div>

      {/* Expandable content */}
      {isExpanded && (
        <div
          css={css`
            margin-bottom: ${spacing.deci};
          `}
        >
          {whyItMatters && (
            <div
              css={css`
                margin-top: ${spacing.deci};
              `}
            >
              <h2
                css={css`
                  color: ${colors.tungsten};
                  margin: 0 0 ${spacing.milli} 0;
                  ${fontStyles.fatCat}
                  html[data-theme='dark'] & {
                    color: ${colors.sugar};
                  }
                `}
              >
                {t('collections.whyItMatters')}
              </h2>
              <p
                css={css`
                  ${fontStyles.thinFlea}
                  color: ${colors.tungsten};
                  line-height: 1.5;
                  margin: 0;
                  html[data-theme='dark'] & {
                    color: ${colors.sugar};
                  }
                `}
              >
                {whyItMatters}
              </p>
            </div>
          )}

          {allocationQuery.data?.strategy && (
            <div
              css={css`
                margin-top: ${spacing.deci};
              `}
            >
              <h2
                css={css`
                  color: ${colors.tungsten};
                  margin: 0 0 ${spacing.milli} 0;
                  ${fontStyles.fatCat}
                  html[data-theme='dark'] & {
                    color: ${colors.sugar};
                  }
                `}
              >
                {t('collections.strategy')}
              </h2>
              <div
                css={css`
                  ${fontStyles.thinFlea}
                  color: ${colors.tungsten};
                  line-height: 1.5;
                  margin: 0;
                  html[data-theme='dark'] & {
                    color: ${colors.sugar};
                  }
                `}
              >
                {allocationQuery.data.strategy}
              </div>
            </div>
          )}

          <button
            onClick={() => setIsExpanded(false)}
            css={css`
              background: none;
              border: none;
              color: ${colors.petrolBlue};
              cursor: pointer;
              padding: 0;
              margin-top: ${spacing.milli};
              ${fontStyles.flea}
              font-weight: 500;

              html[data-theme='dark'] & {
                color: ${colors.skyBlue};
              }

              &:hover {
                text-decoration: underline;
              }
            `}
          >
            {t('collections.readLess')}
          </button>
        </div>
      )}

      {/* All collection investments */}
      <div
        css={css`
          margin-top: ${spacing.deci};
        `}
      >
        <InvestmentTable
          investments={investmentsWithAllocations}
          columnSelections={columnSelections}
          onChangeColumnSelections={(index, value) => {
            if (columnSelections) {
              const updatedSelections = [...columnSelections];
              updatedSelections[index] = value;
              setColumnSelections(updatedSelections);
            }
          }}
          sortingRule={sortingRule}
          onChangeSortingRule={(rule) => {
            userChangedSort.current = true;
            setSortingRule(rule);
          }}
          filters={filters}
          onSetFilter={(key) => {
            const newFilters = { ...filters, [key]: !filters[key] };
            setFilters(newFilters);

            // Show debounced toast message to prevent spam
            const isActive = newFilters[key];
            const filterLabels: Record<FilterKey, string> = {
              stocks: t('collections.filter.stocks'),
              funds: t('collections.filter.funds'),
              large: t('collections.filter.largeCap'),
              quality: t('collections.filter.quality'),
              lowFees: t('collections.filter.lowFees'),
            };
            const action = isActive ? t('collections.filter.applied') : t('collections.filter.removed');
            showFilterToast(`${filterLabels[key]} ${action}`);
          }}
          showExpandReasoning={true}
          availableFilters={availableFilters}
        />
      </div>

      {/* Ask Bloom AI Section */}
      <div
        css={css`
          margin-top: ${spacing.regular};
        `}
      >
        <h2
          css={css`
            ${fontStyles.shark};
            color: var(--primary-text);
            html[data-theme='dark'] & {
              color: ${colors.sugar};
            }
          `}
        >
          {t('collections.askBloomAI')}
        </h2>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${spacing.deci};
            margin-top: ${spacing.regular};
            margin-bottom: ${spacing.deci};
          `}
        >
          <button
            css={css`
              ${fontStyles.mouse}
              font-weight: 400;
              background: ${colors.petrolBlue}10;
              color: ${colors.petrolBlue};
              padding: ${spacing.centi};
              border-radius: 8px;
              text-align: left;
              transition: background 0.2s;
              border: none;
              cursor: pointer;

              &:hover {
                background: ${colors.petrolBlue}20;
              }

              html[data-theme='dark'] & {
                background: ${colors.skyBlue}10;
                color: ${colors.skyBlue};
                &:hover {
                  background: ${colors.skyBlue}20;
                }
              }
            `}
            onClick={() => navigateToChat(t('collections.chatPrompt.catalysts', { name }))}
          >
            {t('collections.ai.catalysts')}
          </button>
          <button
            css={css`
              ${fontStyles.mouse}
              font-weight: 400;
              background: ${colors.petrolBlue}10;
              color: ${colors.petrolBlue};
              padding: ${spacing.centi};
              border-radius: 8px;
              text-align: left;
              transition: background 0.2s;
              border: none;
              cursor: pointer;

              &:hover {
                background: ${colors.petrolBlue}20;
              }

              html[data-theme='dark'] & {
                background: ${colors.skyBlue}10;
                color: ${colors.skyBlue};
                &:hover {
                  background: ${colors.skyBlue}20;
                }
              }
            `}
            onClick={() => navigateToChat(t('collections.chatPrompt.risks', { name }))}
          >
            {t('collections.ai.risks')}
          </button>
          <button
            css={css`
              ${fontStyles.mouse}
              font-weight: 400;
              background: ${colors.petrolBlue}10;
              color: ${colors.petrolBlue};
              padding: ${spacing.centi};
              border-radius: 8px;
              text-align: left;
              transition: background 0.2s;
              border: none;
              cursor: pointer;

              &:hover {
                background: ${colors.petrolBlue}20;
              }

              html[data-theme='dark'] & {
                background: ${colors.skyBlue}10;
                color: ${colors.skyBlue};
                &:hover {
                  background: ${colors.skyBlue}20;
                }
              }
            `}
            onClick={() => navigateToChat(t('collections.chatPrompt.improve', { name }))}
          >
            {t('collections.ai.improve')}
          </button>
        </div>
      </div>

      <FeedbackFooter />
    </>
  );
};

const CollectionPage: FC = () => {
  const { t } = useTranslation();
  const { collectionId } = useParams<{ collectionId?: string }>();
  const history = useHistory();
  const [isCopying, setIsCopying] = useState(false);
  const isMountedRef = useRef(true);

  // Function to navigate to chat with collection context
  const navigateToChat = (customMessage?: string) => {
    if (!query.data) return;

    // Build message with collection name
    let message = customMessage || '';
    if (message && query.data.name) {
      message = message.replace('<Collection Name>', query.data.name);
    }

    // Navigate to chat with the message and source info
    // The chat agent already knows how to get symbols for a collection
    history.push('/chat', {
      message,
      from: 'collection',
    });
  };

  // Track component mount status to prevent state updates after unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const purchaserInfo = stores.Purchase.useState((s) => s.purchaserInfo);
  const isPurchaseInfoLoaded = stores.Purchase.useState((s) => s.isPurchaseInfoLoaded);
  const hasActiveSubscription = useMemo(
    () => !!purchaserInfo?.entitlements?.active[stores.entitlementIdentifier]?.isActive,
    [purchaserInfo?.entitlements?.active],
  );

  const query = useQuery(
    ['investments', 'collections', collectionId],
    async () => (collectionId ? getCollection(collectionId) : undefined),
    { enabled: !!collectionId },
  );

  // Only determine locked state after purchase info has loaded to avoid a race condition
  // where collection data resolves before purchaserInfo, which would briefly show the
  // paywall modal (or restricted content) to subscribers.
  const isRestrictedAndLocked = isPurchaseInfoLoaded && !hasActiveSubscription && !!query.data?.restrictedToSubscribers;

  // Track whether this page instance opened the paywall modal, so we only close
  // modals that we opened (not ones opened by other features).
  const didOpenPaywallModal = useRef(false);

  // Trigger paywall modal when a restricted collection is accessed without a subscription.
  // The isPurchaseInfoLoaded guard ensures we don't fire this for subscribers whose
  // purchase info simply hasn't loaded yet.
  useEffect(() => {
    if (isRestrictedAndLocked) {
      didOpenPaywallModal.current = true;
      stores.showPaymentModalByPreference('collection_page');
    }
  }, [isRestrictedAndLocked]);

  // Close the paywall modal once subscription info confirms the user is a subscriber.
  // Without this, a subscriber who hits the page before the RC SDK initializes would
  // see the modal open (isRestrictedAndLocked briefly true) and it would never close
  // once isPurchaseInfoLoaded becomes true.
  // Only close the modal if this page opened it — guards against closing modals
  // opened by other features when a subscriber navigates directly to a restricted collection.
  useEffect(() => {
    if (
      didOpenPaywallModal.current &&
      !isRestrictedAndLocked &&
      isPurchaseInfoLoaded &&
      hasActiveSubscription &&
      !!query.data?.restrictedToSubscribers
    ) {
      stores.hidePaymentModalByPreference();
    }
  }, [isRestrictedAndLocked, isPurchaseInfoLoaded, hasActiveSubscription, query.data?.restrictedToSubscribers]);

  const handleCopyClick = async () => {
    if (!query.data || !collectionId || isCopying) return;

    if (!isMountedRef.current) return;
    setIsCopying(true);

    try {
      // Fetch allocation data directly
      const allocationData = await getCollectionAllocation(collectionId);

      // Check if still mounted before proceeding
      if (!isMountedRef.current) return;

      // Prepare allocations data
      // Transform API response (uses 'allocation') to storage format (uses 'allocation_percentage')
      const allocations =
        allocationData?.allocations && allocationData.allocations.length > 0
          ? allocationData.allocations.map((a) => ({
              symbol: a.symbol,
              name: a.name || a.symbol,
              allocation_percentage: a.allocation, // Map API field to storage field
              rationale: a.rationale,
            }))
          : query.data.investments.map((inv) => ({
              symbol: inv.symbol,
              name: inv.name || inv.symbol,
              allocation_percentage: undefined,
              rationale: '',
            }));

      // Navigate to confirmation page with state (only if still mounted)
      if (isMountedRef.current) {
        history.push(`/collection/${collectionId}/confirm`, {
          collectionId,
          collectionName: query.data.name,
          strategy: allocationData?.strategy,
          allocations,
        });
      }
    } catch (error) {
      console.error('Error loading collection data:', error);
      if (isMountedRef.current) {
        toast.error(t('collections.failedToLoad'));
      }
    } finally {
      // Always reset loading state if component is still mounted
      if (isMountedRef.current) {
        setIsCopying(false);
      }
    }
  };

  if (!collectionId) return null;

  return (
    <Page showNavigationBar>
      <div
        css={css`
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          background-color: var(--background-color);
          height: 230px;
          z-index: -1;
        `}
      />
      <Header
        headingText=""
        css={css`
          background-color: var(--background-color);
          html[data-theme='dark'] & {
            background-color: ${colors.liquorice};
            color: ${colors.sugar};
          }
        `}
        rightAction={
          // Hide copy button for restricted collections until subscription status is confirmed,
          // preventing a brief window where non-subscribers could click through before
          // isPurchaseInfoLoaded becomes true.
          !query.data?.restrictedToSubscribers || (isPurchaseInfoLoaded && !isRestrictedAndLocked) ? (
            <button
              css={css`
                background: none;
                border: none;
                padding: ${spacing.milli};
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                width: 40px;
                height: 40px;
                color: ${colors.petrolBlue};

                &:hover {
                  opacity: 0.7;
                }

                html[data-theme='dark'] & {
                  color: ${colors.skyBlue};
                }
              `}
              onClick={handleCopyClick}
              aria-label={t('collections.copyToPortfolioAriaLabel')}
              disabled={isCopying}
            >
              <img
                src={CopyIcon}
                alt={t('collections.copyAlt')}
                width="24"
                height="24"
                css={css`
                  filter: invert(45%) sepia(77%) saturate(372%) hue-rotate(134deg) brightness(92%) contrast(91%);

                  html[data-theme='dark'] & {
                    filter: invert(69%) sepia(23%) saturate(1289%) hue-rotate(153deg) brightness(95%) contrast(92%);
                  }
                `}
              />
            </button>
          ) : undefined
        }
      />
      <div
        css={css`
          padding-bottom: ${spacing.deci};
        `}
      >
        {(() => {
          if (query.isLoading)
            return (
              <div
                css={css`
                  text-align: center;
                  padding-top: 40px;
                `}
              >
                <Spinner color={colors.cloud} size="lg" />
              </div>
            );
          if (query.isError || !query.data)
            return (
              <div
                css={css`
                  margin-top: 40vh;
                `}
              >
                <ErrorSection text={t('collections.errorLoading')} onRetry={() => query.refetch()} />
              </div>
            );
          // Show a spinner for restricted collections while we wait for purchase info to
          // load. This prevents restricted content from being briefly visible before we
          // know the user's subscription status.
          if (query.data?.restrictedToSubscribers && !isPurchaseInfoLoaded)
            return (
              <div
                css={css`
                  text-align: center;
                  padding-top: 40px;
                `}
              >
                <Spinner color={colors.cloud} size="lg" />
              </div>
            );
          if (isRestrictedAndLocked)
            return (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  padding: ${spacing.deka} ${spacing.deka};
                  text-align: center;
                  gap: ${spacing.deka};
                  margin-top: 20vh;
                `}
              >
                <div
                  css={css`
                    ${fontStyles.dog};
                    color: var(--text-color);
                  `}
                >
                  🔒 {t('collections.proRequired', 'This collection is for Bloom Pro subscribers.')}
                </div>
                <button
                  css={css`
                    background-color: ${colors.petrolBlue};
                    color: ${colors.sugar};
                    border: none;
                    border-radius: 8px;
                    padding: ${spacing.centi} ${spacing.deka};
                    ${fontStyles.dog};
                    cursor: pointer;
                    &:hover {
                      opacity: 0.85;
                    }
                  `}
                  onClick={() => stores.showPaymentModalByPreference('collection_page')}
                >
                  {t('collections.unlockPro', 'Unlock Bloom Pro')}
                </button>
              </div>
            );
          return <Content {...query.data} collectionId={collectionId} navigateToChat={navigateToChat} />;
        })()}
      </div>
    </Page>
  );
};

export default CollectionPage;
